import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {Box, Container, Heading, SimpleGrid, Text, Spinner, Center, useColorModeValue, Image, Tooltip} from '@chakra-ui/react';
import { Podcast } from '../types/podcast';
import { API_BASE_URL } from '../config/api.ts';

// Default gradient colors for podcasts without images
const gradients = [
  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  'linear-gradient(135deg, #2193b0 0%, #6dd5ed 100%)',
  'linear-gradient(135deg, #ee0979 0%, #ff6a00 100%)',
  'linear-gradient(135deg, #834d9b 0%, #d04ed6 100%)',
  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
];

export const PodcastList = () => {
  const [podcasts, setPodcasts] = useState<Podcast[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());

  const cardBg = useColorModeValue('white', 'gray.800');
  const cardHoverBg = useColorModeValue('gray.50', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'white');
  const mutedTextColor = useColorModeValue('gray.500', 'gray.400');

  const fetchPodcasts = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const endpoint = `${API_BASE_URL}/api/podcasts`;
      console.log('Fetching podcasts from:', endpoint);
      
      const response = await fetch(endpoint);
      if (!response.ok) {
        console.error(`HTTP error! status: ${response.status}`);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Check if we got an error response
      if (data.error) {
        console.error('Error response from API:', data.error);
        throw new Error(data.error);
      }
      
      // Validate data is an array
      if (!Array.isArray(data)) {
        console.error('Invalid data format - not an array:', data);
        throw new Error('Invalid data format from API');
      }
      
      console.log('Received podcasts from server:', data.length, 'podcasts');
      if (data.length > 0) {
        console.log('Sample podcast:', {
          name: data[0]?.podcast_name,
          episode_count: data[0]?.episode_count,
          guid: data[0]?.podcast_guid
        });
      } else {
        console.warn('No podcasts returned from the API');
      }
      
      // Sort podcasts by last_episode_date
      const sortedPodcasts = [...data].sort((a, b) => {
        // Safely handle missing dates
        if (!a.last_episode_date) return 1;
        if (!b.last_episode_date) return -1;
        
        try {
          const dateA = new Date(a.last_episode_date);
          const dateB = new Date(b.last_episode_date);
          return dateB.getTime() - dateA.getTime();
        } catch (err) {
          console.error('Error sorting dates:', err);
          return 0;
        }
      });
      
      setPodcasts(sortedPodcasts);
    } catch (error) {
      console.error('Error fetching podcasts:', error);
      setError('Failed to load podcasts. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPodcasts();
  }, []);

  const handleImageError = (podcastGuid: string) => {
    console.log(`Image error for podcast: ${podcastGuid}`);
    setImageErrors(prev => new Set([...prev, podcastGuid]));
  };

  const getGradientBackground = (podcast: Podcast) => {
    // Use a hash of the podcast guid to pick a consistent gradient
    const hash = podcast.podcast_guid.split('').reduce((acc, char, idx) => {
      return acc + char.charCodeAt(0) * (idx + 1);
    }, 0);
    const gradientIndex = Math.abs(hash) % gradients.length;
    return gradients[gradientIndex];
  };

  const getDaysAgo = (dateString: string | null) => {
    if (!dateString) return '';
    
    const publishedDate = new Date(dateString);
    const today = new Date();
    
    // Reset hours to compare just the dates
    publishedDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    
    const diffTime = today.getTime() - publishedDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    return `${diffDays}d ago`;
  };

  if (isLoading) {
    return (
      <Center py={8}>
        <Spinner size="xl" />
      </Center>
    );
  }

  if (error) {
    return (
      <Center py={8}>
        <Text color="red.500">{error}</Text>
      </Center>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <Box mb={8} textAlign="left">
        <Heading size="xl" color={textColor}>
          Discover
        </Heading>
      </Box>

      <SimpleGrid columns={{ base: 2, sm: 3, md: 4, lg: 5 }} spacing={4}>
        {podcasts.map((podcast) => {
          const hasImageError = imageErrors.has(podcast.podcast_guid);
          const gradientBg = getGradientBackground(podcast);

          return (
            <Link
              key={podcast.podcast_guid}
              to={`/podcasts/${encodeURIComponent(podcast.podcast_guid)}`}
              style={{ textDecoration: 'none' }}
            >
              <Box
                bg={cardBg}
                borderRadius="lg"
                overflow="hidden"
                transition="all 0.2s"
                _hover={{
                  transform: 'translateY(-4px)',
                  shadow: 'lg',
                  bg: cardHoverBg,
                }}
                height="100%"
                display="flex"
                flexDirection="column"
              >
                <Box
                  width="100%"
                  paddingBottom="100%"
                  bg={hasImageError ? gradientBg : undefined}
                  position="relative"
                >
                  {!hasImageError && (
                    <Image
                      src={podcast.image_url}
                      alt={`${podcast.podcast_name} cover`}
                      position="absolute"
                      top={0}
                      left={0}
                      width="100%"
                      height="100%"
                      objectFit="cover"
                      onError={() => handleImageError(podcast.podcast_guid)}
                    />
                  )}
                </Box>

                <Box p={1}>
                  <Tooltip 
                    label={podcast.podcast_name}
                    placement="top"
                    hasArrow
                    openDelay={300}
                  >
                    <Text
                      fontSize="sm"
                      fontWeight="semibold"
                      color={textColor}
                      isTruncated
                      maxW="100%"
                      title={podcast.podcast_name}
                    >
                      {podcast.podcast_name}
                    </Text>
                  </Tooltip>
                  <Text fontSize="xs" color={mutedTextColor} mt={1}>
                    {podcast.episode_count} {podcast.episode_count === 1 ? 'episode' : 'episodes'}
                    {podcast.last_episode_date && (
                      <> • Last episode {getDaysAgo(podcast.last_episode_date)}</>
                    )}
                  </Text>
                </Box>
              </Box>
            </Link>
          );
        })}
      </SimpleGrid>
    </Container>
  );
}; 